<script>
import { mapGetters, mapState } from 'vuex'

import SelectFieldStock from '@/components/common/organims/SelectFieldStock.vue'
import SelectStamp from '@/components/addShooting/SelectStamp.vue'
import SelectFormat from '@/components/addShooting/SelectFormat.vue'
import SelectModel from '@/components/addShooting/SelectModel.vue'
import ViewImageUpload from '@/components/addShooting/ViewImageUpload.vue'
import DragAndDrop from '@/components/addShooting/DragAndDrop.vue'
import RegistrationSelect from '@/components/addShooting/RegistrationSelect.vue'
import ModalSendShooting from '@/components/addShooting/ModalSendShooting.vue'
import Loader from '@/components/Loader'
import BackButton from '@/components/BackButton'

export default {
  name: 'AddShooting',
  components: {
    SelectFieldStock,
    SelectStamp,
    SelectModel,
    ViewImageUpload,
    DragAndDrop,
    RegistrationSelect,
    ModalSendShooting,
    Loader,
    BackButton,
    SelectFormat
  },

  data() {
    return {
      attemptedSubmit: false
    }
  },

  computed: {
    ...mapGetters(['userId']),
    ...mapState({
      owners: (state) => state.owners,
      loading: (state) => state.loading,
      status: (state) => state.newShooting.status,
      owner: (state) => state.newShooting.owner,
      registration: (state) => state.newShooting.registration,
      stampExist: (state) => !!(state.newShooting && state.newShooting.stamp),
      stamps: (state) => state.stamps,
      format: (state) => state.newShooting.format,
      shooting: (state) => state.newShooting.shooting,
      labelsOwner: (state) => state.labelsOwner,
      products: (state) => state.products,
      model: (state) => state.newShooting.model,
      viewImages: (state) => state.newShooting.viewImages,
      userDetails: (state) => state.usersConfig.userDetails
    }),
    isNewFlow() {
      return this.userDetails && this.userDetails.newFlow === true
    },
    hasRegistrationAndFormat() {
      return this.registration !== '' && this.format !== ''
    },
    hasOwnerAndStamp() {
      return this.owner !== '' && this.stampExist
    },
    isStockSelectionRequired() {
      return this.active && this.labelsOwner.length > 1
    },
    // stock is resolved only once owner labels are loaded and an owner has been set
    hasStockResolved() {
      if (this.active && this.labelsOwner.length === 0) return false
      return this.owner !== ''
    },
    mandatoryMasks() {
      if (!this.model || !this.model.masks) return []
      return this.model.masks.filter((mask) => !mask.options?.optional)
    },
    sortedMasks() {
      if (!this.model?.masks) return []
      return [...this.model.masks].sort((a, b) => (a.displayOrder ?? a.order) - (b.displayOrder ?? b.order))
    },
    allMandatoryUploaded() {
      return this.mandatoryMasks.every((mask) => !!this.viewImages[mask.order])
    },
    valid() {
      if (this.isNewFlow) {
        return (
          this.registration !== '' &&
          this.hasStockResolved &&
          this.owner !== '' &&
          this.stampExist &&
          this.model !== null &&
          this.model.masks.length > 0 &&
          this.allMandatoryUploaded
        )
      }
      return this.registration !== '' && this.owner !== '' && this.stampExist && this.shooting.length > 0
    },
    active() {
      return this.status === 'create'
    }
  },

  created() {
    this.getProducts()
    this.getLabelOwner(this.owners)
  },

  // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
  beforeDestroy() {
    this.$store.commit('RESET_ADD_SHOOTING_FORM')
  },

  watch: {
    // Stamps are only fetched for the legacy flow; in the new flow, SelectModel sets stamps based on the selected model.
    format(newFormat) {
      if (!this.isNewFlow && newFormat && this.userId) {
        this.$store.dispatch('getStamps', { userId: this.userId, format: newFormat })
      }
    },
    // When model changes in new flow and stock dropdown is visible, reset owner
    // so user must explicitly pick from the stock dropdown (not carry over registration-set owner)
    model(newModel) {
      if (this.isNewFlow && newModel && this.isStockSelectionRequired) {
        this.$store.commit('UPDATE_OWNER', '')
      }
    }
  },

  methods: {
    async getLabelOwner(owners) {
      const res = await this.$store.dispatch('getLabelOwner', owners)

      if (!res || this.labelsOwner.length === 0) return
      else if (this.labelsOwner.length === 1) {
        const owner = this.labelsOwner[0].ownerId
        this.$store.commit('UPDATE_OWNER', owner)
      }
    },
    getProducts(owners) {
      this.$store.dispatch('getProducts', owners)
    },
    changeValue(ob) {
      const { name, value } = ob
      this.$store.commit('UPDATE_OWNER', value)
    },
    onCreateAttempt() {
      this.attemptedSubmit = true
    }
  }
}
</script>

<template>
  <v-container style="height: 100%" fluid>
    <BackButton />

    <div v-if="loading" class="loader-block">
      <Loader />
    </div>

    <div v-else>
      <!-- Title -->
      <v-row justify="center">
        <v-col cols="12" sm="12" md="6">
          <v-card flat>
            <v-card-title class="justify-center">{{ $t('addShooting.addShooting') }}</v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- search Immat -->
      <v-row justify="center">
        <v-col cols="12" sm="12" md="6">
          <RegistrationSelect :registrations="products" />
        </v-col>
      </v-row>

      <!-- select format -->
      <v-row justify="center">
        <v-col cols="12" sm="12" md="6">
          <SelectFormat :registrations="products" />
        </v-col>
      </v-row>

      <!-- ==================== NEW FLOW ==================== -->
      <template v-if="isNewFlow">
        <!-- Select Model -->
        <v-row v-if="hasRegistrationAndFormat" justify="center">
          <v-col cols="12" sm="12" md="6">
            <SelectModel />
          </v-col>
        </v-row>

        <template v-if="hasRegistrationAndFormat && model">
          <!-- Owner -->
          <v-row v-if="active" justify="center">
            <v-col cols="12" sm="12" md="6">
              <SelectFieldStock v-on:change-value="changeValue" :owners="labelsOwner" />
            </v-col>
          </v-row>

          <!-- Select Stamp — shown only after stock is resolved -->
          <v-row v-if="hasStockResolved" justify="center">
            <v-col cols="12" sm="12" md="6">
              <SelectStamp />
            </v-col>
          </v-row>

          <!-- View uploads (shown only after stock resolved, owner and stamp are filled) -->
          <v-row v-if="hasStockResolved && hasOwnerAndStamp" justify="center">
            <v-col cols="12" sm="12" md="6">
              <ViewImageUpload
                v-for="mask in sortedMasks"
                :key="mask.order"
                :mask="mask"
                :show-validation="attemptedSubmit"
              />
            </v-col>
          </v-row>
        </template>
      </template>

      <!-- ==================== LEGACY FLOW ==================== -->
      <template v-else>
        <!-- Owner -->
        <v-row v-if="active && hasRegistrationAndFormat" justify="center">
          <v-col cols="12" sm="12" md="6">
            <SelectFieldStock v-on:change-value="changeValue" :owners="labelsOwner" />
          </v-col>
        </v-row>

        <!-- Select Stamp -->
        <v-row v-if="hasRegistrationAndFormat" justify="center">
          <v-col cols="12" sm="12" md="6">
            <SelectStamp />
          </v-col>
        </v-row>

        <!-- Drag and drop photos -->
        <v-row v-if="hasRegistrationAndFormat && stampExist" justify="center">
          <v-col cols="12" sm="12" md="6">
            <DragAndDrop />
          </v-col>
        </v-row>
      </template>

      <!-- Send shooting -->
      <v-row justify="center">
        <v-col cols="12" sm="12" md="6">
          <div class="end-block">
            <ModalSendShooting :active="active" :valid="valid" @create-attempt="onCreateAttempt" />
          </div>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<style lang="sass" scoped>
.backButton-block
  padding: 20px

.loader-block
  width: 100%
  height: 100%
  display: flex
  justify-content: center
  align-items: center

.end-block
  display: flex
  justify-content: flex-end

p
  text-align: center
  font-size: 16px
  color: rgba(0, 0, 0, 0.7)
</style>
